import { useState, useMemo } from "react";

/**
 * usePagination
 *
 * Generic pagination hook — works with any filtered array.
 *
 * @param {Array}  data        - the full (already-filtered) array to paginate
 * @param {number} pageSize    - rows per page (default 10)
 *
 * Returns:
 *   paged        - slice of `data` for the current page
 *   page         - current page number (1-based)
 *   totalPages   - total number of pages
 *   pageSize     - current rows-per-page value
 *   setPage      - jump to a page
 *   setPageSize  - change rows per page (also resets to page 1)
 *   pageStart    - index of first row on current page (1-based, for display)
 *   pageEnd      - index of last row on current page (1-based, for display)
 *   totalItems   - total items count
 */
export function usePagination(data, defaultPageSize = 10) {
  const [page, setPageRaw]         = useState(1);
  const [pageSize, setPageSizeRaw] = useState(defaultPageSize);

  /* When data shrinks (e.g. search), clamp page so we never show empty pages */
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const safePage   = Math.min(page, totalPages);

  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, safePage, pageSize]);

  const setPage = (p) => setPageRaw(Math.max(1, Math.min(p, totalPages)));

  const setPageSize = (size) => {
    setPageSizeRaw(size);
    setPageRaw(1); // reset to first page on size change
  };

  const pageStart = data.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const pageEnd   = Math.min(safePage * pageSize, data.length);

  return {
    paged,
    page: safePage,
    totalPages,
    pageSize,
    setPage,
    setPageSize,
    pageStart,
    pageEnd,
    totalItems: data.length,
  };
}